import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createSprint,
  fetchSprints,
  setSprintActive,
  setSprintClosed,
  updateSprint,
  type CreateSprintInput,
  type UpdateSprintInput,
} from '../api/sprint.api';

export const SPRINT_KEYS = {
  list: (projectId: string) => ['sprints', projectId] as const,
};

export function useSprints(projectId: string) {
  return useQuery({ queryKey: SPRINT_KEYS.list(projectId), queryFn: () => fetchSprints(projectId), enabled: Boolean(projectId) });
}

export function useCreateSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSprintInput) => createSprint(projectId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SPRINT_KEYS.list(projectId) }),
  });
}

export function useUpdateSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sprintId, data }: { sprintId: string; data: UpdateSprintInput }) => updateSprint(projectId, sprintId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SPRINT_KEYS.list(projectId) }),
  });
}

export function useSetSprintActive(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sprintId, active }: { sprintId: string; active: boolean }) => setSprintActive(projectId, sprintId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SPRINT_KEYS.list(projectId) }),
  });
}

export function useSetSprintClosed(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sprintId, closed }: { sprintId: string; closed: boolean }) => setSprintClosed(projectId, sprintId, closed),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SPRINT_KEYS.list(projectId) }),
  });
}
